import React from 'react';
import * as Linking from 'expo-linking';
import Text, { TextProps } from './Text';
import PressableDynamicOpacity from './PressableDynamicOpacity';
import useNotify from '../../hooks/useNotify';
import { getIntl } from '../../utils/intl';
import { Platform } from 'react-native';
import { useIsInPrompt } from './Prompt/PromptContainer';
import { useIsMobileLayout } from '../../hooks/useIsMobileLayout';
import { usePromptButton } from '../shared/PromptButton';
import { ContentStack } from './Stack';
import PromptActions from './Prompt/PromptActions';

const intl = getIntl('Link', {
    unableToOpenUrl: 'Unable to open link',
    phone: 'Phone',
    sms: 'SMS Text Message',
});

export type LinkOwnProps = {
    onPress: () => void;
    testID?: string;
    isDisabled?: boolean;
    linkColor?: 'pending' | 'activated' | 'blue' | 'visited';
    noUnderline?: boolean;
};

export type LinkProps = LinkOwnProps & TextProps;

const Link: React.FC<LinkProps> = ({ onPress, testID, isDisabled, linkColor, ...toPass }) => {
    return (
        <PressableDynamicOpacity onPress={onPress} testID={testID} isDisabled={isDisabled}>
            <Text
                {...toPass}
                underline
                color={
                    linkColor === 'pending'
                        ? 'Primary-Button-Default'
                        : linkColor === 'activated'
                        ? 'Primary-Button-Press'
                        : linkColor === 'blue'
                        ? 'Primary'
                        : linkColor === 'visited'
                        ? 'Gray-Text'
                        : undefined
                }
            />
        </PressableDynamicOpacity>
    );
};

export default React.memo(Link);

export type LinkType = 'url' | 'email' | 'phone' | 'address';

// User-entered website values (e.g. "www.example.com") often omit the scheme. Without one the
// browser treats the value as a relative path and navigates within the app instead of out to the
// external site. Default missing-scheme values to https:// so they open externally.
export const ensureUrlHasScheme = (url: string): string => {
    const trimmed = url.trim();
    if (!trimmed) return trimmed;
    // Already has an explicit scheme like https://, http://, ftp:// → leave untouched.
    if (/^[a-z][a-z\d+.-]*:\/\//i.test(trimmed)) return trimmed;
    // Protocol-relative URL (//example.com) → assume https.
    if (trimmed.startsWith('//')) return `https:${trimmed}`;
    return `https://${trimmed}`;
};

export type ClickableLinkTextProps = Omit<LinkProps, 'onPress' | 'children'> & {
    linkType: LinkType;
    children: string;
    linkLocation?: string;
};

export const ClickableLinkText: React.FC<ClickableLinkTextProps> = (props) => {
    const isWeb = Platform.OS === 'web';
    const isMobile = useIsMobileLayout();
    const isInPrompt = useIsInPrompt();
    const { linkType, linkLocation, ...toPass } = props;
    const notify = useNotify();
    const location = linkLocation ? linkLocation : toPass.children;
    const openLink = (link: string) => {
        Linking.openURL(link).catch(() => {
            // initially tried using canOpenUrl but it doesn't work for phone numbers
            notify('error', intl('unableToOpenUrl'));
        });
    };

    const mapsBase = Platform.OS === 'ios' ? 'https://maps.apple.com' : 'https://maps.google.com';

    const urlMap: Record<LinkType | 'sms', string> = {
        url: ensureUrlHasScheme(location),
        email: `mailto:${encodeURIComponent(location)}`,
        phone: `tel:${location}`,
        sms: `sms:${location}`,
        address: `${mapsBase}/?q=${encodeURIComponent(location)}`,
    };

    const smsOrPhonePrompt = usePromptButton({
        renderPromptContents: (onCloseActionSheet) => (
            <ContentStack>
                <PromptActions
                    actions={[
                        {
                            title: intl('phone'),
                            onPress: () => {
                                openLink(urlMap['phone']);
                                onCloseActionSheet();
                            },
                            icon: 'Phone',
                            variant: 'secondary',
                        },
                        {
                            title: intl('sms'),
                            onPress: () => {
                                openLink(urlMap['sms']);
                                onCloseActionSheet();
                            },
                            variant: 'secondary',
                            icon: 'MessageSquare',
                        },
                    ]}
                />
            </ContentStack>
        ),
        promptTitle: props.linkLocation || props.children,
    });

    const isPhoneEnabled = !isWeb || (isWeb && isMobile);
    // need prompt to select sms or phone call, we can't have nested prompt
    const isSMSOrPhonePromptEnabled = isPhoneEnabled && !isInPrompt;

    if (linkType !== 'phone') {
        return (
            <Link
                onPress={() => {
                    openLink(urlMap[linkType]);
                }}
                {...toPass}
            />
        );
    } else if (!isPhoneEnabled) {
        return <Text {...toPass} />;
    } else if (!isSMSOrPhonePromptEnabled) {
        return (
            <Link
                onPress={() => {
                    openLink(urlMap[linkType]);
                }}
                {...toPass}
            />
        );
    } else {
        return (
            <>
                <Link
                    onPress={() => {
                        smsOrPhonePrompt.onPress();
                    }}
                    {...toPass}
                />
                {smsOrPhonePrompt.Prompt}
            </>
        );
    }
};
